'use client';

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import type { ManualCompetitor } from './CompetitorLocationsManager';

interface CompetitorVisualizationChartsProps {
  competitors: ManualCompetitor[];
}

// ─── Color scales ──────────────────────────────────────────────────────────────
const DENSITY_COLORS = ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8', '#1E3A8A'];
const THREAT_COLORS: Record<string, string> = {
  large: '#EF4444',
  medium: '#F59E0B',
  small: '#6B7280',
};
const SATURATION_GRADIENT = ['#D1FAE5', '#6EE7B7', '#10B981', '#059669', '#065F46'];

function getDensityColor(count: number, max: number): string {
  const idx = max === 0 ? 0 : Math.min(Math.floor((count / max) * (DENSITY_COLORS.length - 1)), DENSITY_COLORS.length - 1);
  return DENSITY_COLORS[idx];
}

function getSaturationColor(score: number): string {
  const idx = Math.min(Math.floor(score / 20), SATURATION_GRADIENT.length - 1);
  return SATURATION_GRADIENT[idx];
}

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
const DensityTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }} className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
          {p.name}: <span className="font-bold ml-1">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const SaturationTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const score = payload[0]?.value ?? 0;
  const level = score >= 80 ? 'Very High' : score >= 60 ? 'High' : score >= 40 ? 'Moderate' : score >= 20 ? 'Low' : 'Very Low';
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-800 mb-1">{label}</p>
      <p className="text-gray-600">Saturation Score: <span className="font-bold text-gray-900">{score}</span></p>
      <p className="text-gray-600">Level: <span className="font-bold" style={{ color: getSaturationColor(score) }}>{level}</span></p>
    </div>
  );
};

export default function CompetitorVisualizationCharts({ competitors }: CompetitorVisualizationChartsProps) {
  // ─── Derived data ────────────────────────────────────────────────────────────
  const cityDensityData = useMemo(() => {
    const map: Record<string, { city: string; large: number; medium: number; small: number; total: number }> = {};
    competitors.forEach((c) => {
      const key = `${c.city}, ${c.state}`;
      if (!map[key]) map[key] = { city: key, large: 0, medium: 0, small: 0, total: 0 };
      map[key][c.size]++;
      map[key].total++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [competitors]);

  const threatDistributionData = useMemo(() => {
    const map: Record<string, { city: string; threatScore: number; large: number; medium: number; small: number }> = {};
    competitors.forEach((c) => {
      const key = `${c.city}, ${c.state}`;
      if (!map[key]) map[key] = { city: key, threatScore: 0, large: 0, medium: 0, small: 0 };
      map[key][c.size]++;
      // Weighted threat score: large=3, medium=2, small=1
      map[key].threatScore += c.size === 'large' ? 3 : c.size === 'medium' ? 2 : 1;
    });
    return Object.values(map).sort((a, b) => b.threatScore - a.threatScore).slice(0, 10);
  }, [competitors]);

  const saturationData = useMemo(() => {
    const map: Record<string, { city: string; total: number; large: number }> = {};
    competitors.forEach((c) => {
      const key = `${c.city}, ${c.state}`;
      if (!map[key]) map[key] = { city: key, total: 0, large: 0 };
      map[key].total++;
      if (c.size === 'large') map[key].large++;
    });
    return Object.values(map)
      .map((d) => ({
        city: d.city,
        // Saturation: blend of total count (60%) + large competitor weight (40%), capped at 100
        saturationScore: Math.min(Math.round((d.total * 8) + (d.large * 15)), 100),
        total: d.total,
      }))
      .sort((a, b) => b.saturationScore - a.saturationScore)
      .slice(0, 10);
  }, [competitors]);

  const maxDensity = useMemo(() => Math.max(...cityDensityData.map((d) => d.total), 1), [cityDensityData]);

  if (competitors.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-center">
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-500">Add competitors to see density, threat, and saturation charts</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ─── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <div className="w-1 h-6 rounded-full bg-gradient-to-b from-[#E89A3C] to-[#1D4ED8]" />
        <h2 className="text-base font-semibold text-gray-900">Competitor Market Visualizations</h2>
        <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
          {competitors.length} competitor{competitors.length !== 1 ? 's' : ''} tracked
        </span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* ─── Chart 1: Competitor Density Heatmap (stacked bar) ──────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-800">Competitor Density by City</h3>
            <p className="text-xs text-gray-500 mt-0.5">Number of tracked competitors per market — color intensity reflects density</p>
          </div>

          {/* Heatmap grid */}
          <div className="grid gap-1.5 mb-4">
            {cityDensityData.map((d) => (
              <div key={d.city} className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-28 truncate shrink-0" title={d.city}>{d.city}</span>
                <div className="flex-1 flex gap-0.5 h-6 rounded overflow-hidden">
                  {Array.from({ length: d.total }).map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-sm transition-all"
                      style={{ background: getDensityColor(i + 1, maxDensity) }}
                      title={`${d.city}: ${d.total} competitors`}
                    />
                  ))}
                  {Array.from({ length: Math.max(0, maxDensity - d.total) }).map((_, i) => (
                    <div key={`empty-${i}`} className="flex-1 rounded-sm bg-gray-100" />
                  ))}
                </div>
                <span className="text-xs font-bold text-gray-700 w-5 text-right">{d.total}</span>
              </div>
            ))}
          </div>

          {/* Density legend */}
          <div className="flex items-center gap-1 mt-2">
            <span className="text-xs text-gray-400">Low</span>
            <div className="flex gap-0.5 flex-1">
              {DENSITY_COLORS.map((c) => (
                <div key={c} className="flex-1 h-2 rounded-sm" style={{ background: c }} />
              ))}
            </div>
            <span className="text-xs text-gray-400">High</span>
          </div>
        </div>

        {/* ─── Chart 2: Threat Distribution by City ───────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-800">Threat Distribution by City</h3>
            <p className="text-xs text-gray-500 mt-0.5">Stacked breakdown of competitor sizes — large competitors carry highest threat weight</p>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={threatDistributionData} margin={{ top: 4, right: 8, left: -20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis
                dataKey="city"
                tick={{ fontSize: 10, fill: '#6B7280' }}
                angle={-35}
                textAnchor="end"
                interval={0}
                tickFormatter={(v) => v.split(',')[0]}
              />
              <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false} />
              <Tooltip content={<DensityTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                iconType="circle"
                iconSize={8}
              />
              <Bar dataKey="large" name="Large" stackId="a" fill={THREAT_COLORS.large} radius={[0, 0, 0, 0]} />
              <Bar dataKey="medium" name="Medium" stackId="a" fill={THREAT_COLORS.medium} radius={[0, 0, 0, 0]} />
              <Bar dataKey="small" name="Small" stackId="a" fill={THREAT_COLORS.small} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ─── Chart 3: Market Saturation Trends ──────────────────────────────── */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Market Saturation by City</h3>
            <p className="text-xs text-gray-500 mt-0.5">Composite saturation score (0–100) based on competitor count and size weighting — higher = more saturated market</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-200 inline-block" />Low</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />Moderate</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-800 inline-block" />High</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={saturationData} margin={{ top: 4, right: 16, left: -20, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis
              dataKey="city"
              tick={{ fontSize: 10, fill: '#6B7280' }}
              angle={-35}
              textAnchor="end"
              interval={0}
              tickFormatter={(v) => v.split(',')[0]}
            />
            <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} domain={[0, 100]} />
            <Tooltip content={<SaturationTooltip />} />
            <Bar dataKey="saturationScore" name="Saturation Score" radius={[4, 4, 0, 0]}>
              {saturationData.map((entry) => (
                <Cell key={entry.city} fill={getSaturationColor(entry.saturationScore)} />
              ))}
              <LabelList dataKey="saturationScore" position="top" style={{ fontSize: 10, fill: '#374151', fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Saturation legend bar */}
        <div className="flex items-center gap-1 mt-3">
          <span className="text-xs text-gray-400">0</span>
          <div className="flex gap-0.5 flex-1 h-2 rounded overflow-hidden">
            {SATURATION_GRADIENT.map((c) => (
              <div key={c} className="flex-1" style={{ background: c }} />
            ))}
          </div>
          <span className="text-xs text-gray-400">100</span>
        </div>
      </div>
    </div>
  );
}
